<script setup>
import axios from "axios";
import { onMounted, ref, watch } from "vue";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

// Reactive state for dropdown options
const requestManagers = ref([]);
const requestFitters = ref([]);
const requestStatuses = ref([]);

// Reactive state for date inputs
const startDate = ref("");
const endDate = ref("");

// Reactive state for selected fields
const selected_fields = ref({
  fitterId: '',
  managerId: '',
  startDate: '',
  endDate: '',
});
const requestTotalCount = ref(0);
const requestCompletedCount = ref(0);
const requestNotCompletedCount = ref(0);
const requestRepeated = ref(0);
const requestCountsByKind = ref(0);

let chartInstance = null;
const chartLegend = ref(null);

// Fetch related lists
const fetchRequestRelatedLists = async () => {
  try {
    const response = await axios.get('request_related_lists/');

    // Check if the response data exists and has the expected structure
    if (response.data) {
      requestFitters.value = response.data.request_fitters || [];
    } else {
      console.error('Unexpected API response structure:', response.data);
    }
  } catch (error) {
    console.error('Error fetching related lists:', error);
  }
};

// Fetch statistics based on selected fields
const fetchStatistics = async () => {
  try {
    console.log(selected_fields.value); // Log selected fields for debugging
    const response = await axios.get('fitter_stats/', {
      params: {
        fitter_id: selected_fields.value.fitterId,
        start_date: selected_fields.value.startDate,
        end_date: selected_fields.value.endDate,
      }
    }).then((response) => {
      requestTotalCount.value = response.data.request_total_count;
      requestCompletedCount.value = response.data.request_completed_count
      requestNotCompletedCount.value = response.data.request_not_completed_count
      requestRepeated.value = response.data.request_repeated_count
      requestCountsByKind.value = response.data.request_counts_by_kind
    });

    console.log('Statistics Response:', response.data); // Log the response for debugging
  } catch (error) {
    console.error('Error fetching statistics:', error);
  }
};

// Destroy the existing chart instance
const destroyChart = () => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
};

const createChart = () => {
  destroyChart();

  chartInstance = new Chart(chartLegend.value, {
    type: 'legend',
    data:{
      labels: []
    }
  });
}

// Reset date inputs and selected fields
const resetData = () => {
  startDate.value = "";
  endDate.value = "";
  selected_fields.value.startDate = "";
  selected_fields.value.endDate = "";
};

// Watch for changes in startDate and endDate
watch([startDate, endDate], ([newStartDate, newEndDate]) => {
  selected_fields.value.startDate = newStartDate;
  selected_fields.value.endDate = newEndDate;
  fetchStatistics(); // Fetch statistics whenever dates change
});

// Fetch related lists when the component is mounted
onMounted(() => {
  fetchRequestRelatedLists();
});
</script>

<template>
  <div class="bg-white">
    <!-- Fitter Dropdown with Icon -->
    <div class="form-group position-relative">
      <label class="text-secondary mb-1">Монтер</label>
      <div class="input-group">
      <span class="input-group-text bg-primary text-white">
        <i class="fas fa-user"></i>
      </span>
        <select
            v-model="selected_fields.fitterId"
            @change="fetchStatistics"
            class="form-select form-select-sm"
        >
          <option value="" selected disabled>Выберите монтера</option>
          <option
              v-for="fitter in requestFitters"
              :key="fitter.id"
              :value="fitter.fitter_id"
          >
            {{ fitter.fitter_name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Date Range Selector -->
    <div class="border-0 bg-white mb-4">
      <div class="row g-3">
        <div class="col-md-4">
          <label for="start-date" class="form-label text-secondary">От:</label>
          <div class="input-group">
          <span class=" d-md-none d-lg-block input-group-text bg-primary text-white">
            <i class="fas fa-calendar-alt"></i>
          </span>
            <input
                id="start-date"
                type="date"
                v-model="startDate"
                class="form-control form-control-sm"
            />
          </div>
        </div>
        <div class="col-md-4">
          <label for="end-date" class="form-label text-secondary">До:</label>
          <div class="input-group">
          <span class="d-none d-sm-block  input-group-text bg-primary text-white">
            <i class="fas fa-calendar-alt"></i>
          </span>
            <input
                id="end-date"
                type="date"
                v-model="endDate"
                class="form-control form-control-sm"
            />
          </div>
        </div>
        <div class="col-md-4 d-flex align-items-end">
          <button
              class="btn btn-sm btn-outline-danger w-100"
              @click.prevent="resetData"
          >
            Сбросить
          </button>
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="row g-3">
      <div class="col-3">
        <div class="h-100 border-0 ">
          <div class="text-center">
            <div class="text-primary mb-2">
              <i class="fas fa-clipboard-list fa-2x"></i>
            </div>
            <h6 class="mb-2 text-secondary fs-sm-6">Общее кол-во заявок</h6>
            <h4 class="mb-0 fw-bold">{{ requestTotalCount }}</h4>
          </div>
        </div>
      </div>
      <div class="col-3">
        <div class="h-100 border-0">
          <div class="text-center">
            <div class="text-success mb-2">
              <i class="fas fa-check-circle fa-2x"></i>
            </div>
            <h6 class="mb-2 text-secondary fs-sm-6">Завершенные</h6>
            <h4 class="mb-0 fw-bold">{{ requestCompletedCount }}</h4>
          </div>
        </div>
      </div>
      <div class="col-3">
        <div class="h-100 border-0">
          <div class="text-center">
            <div class="text-warning mb-2">
              <i class="fas fa-hourglass-half fa-2x"></i>
            </div>
            <h6 class="mb-2 text-secondary fs-sm-6">Не завершенные</h6>
            <h4 class="mb-0 fw-bold">{{ requestNotCompletedCount }}</h4>
          </div>
        </div>
      </div>
      <div class="col-3">
        <div class="h-100 border-0">
          <div class="text-center">
            <div class="text-secondary-emphasis mb-2">
              <i class="fas fa-redo-alt fa-2x"></i>
            </div>
            <h6 class="mb-2 text-secondary fs-sm-6">Повторные заявки</h6>
            <h4 class="mb-0 fw-bold">{{ requestRepeated }}</h4>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div v-if="requestCountsByKind" class="mt-2">
      <table class="table table-bordered table-hover table-responsive-sm shadow-sm">
        <thead class="bg-light">
        <tr>
          <th class="text-secondary">Тип заявки</th>
          <th class="text-secondary">Количество</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="item in requestCountsByKind" :key="item.id">
          <td>{{ item.request_kind_name }}</td>
          <td>{{ item.count }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
</style>